import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Button, FunctionalLoader, Page, PageContentHeader, PageEmptyState, Typography, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import LoadingIndicator from '~/components/LoadingIndicator';
import { useStore, useStoreActions } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import BillingHub from '~/pages/AgreementMinisite/BillingHub/BillingHub';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import RestfulWrapper from '../../components/RestfulWrapper/RestfulWrapper';
import SeeWhatItsLike from '../../components/SeeWhatItsLike';
import { BillingHubUrlParams } from '../AgreementMinisite/BillingHub/BillingHub.utils';
import { BulkBillingModal } from './BulkBillingModal/BulkBillingModal';
import { GBHMapEntitiesCallout } from './GBHMapEntitiesCallout/GBHMapEntitiesCallout';
import styles from './GlobalBillingHub.module.scss';
import { getClientOptions, getAgreementOptions, getDefaultRelationshipId } from './GlobalBillingHub.utils';
import { GlobalBillingHubHeader } from './GlobalBillingHubHeader/GlobalBillingHubHeader';
import GlobalBillingHubEmptyStateIcon from './img/GlobalBillingHubEmptyStateIcon';
import img1 from './img/globalBillingHubPreview1.png';
import img2 from './img/globalBillingHubPreview2.png';
const seeWhatItsLikeSteps = [
    {
        title: 'A single place for managing client billing',
        subtitle: 'Easily view and update upcoming invoices for all of your clients in one place.',
        image: {
            src: img1,
            width: 850,
            height: 497
        }
    },
    {
        title: 'Keep payments on autopilot, even when making changes',
        subtitle: "Add or remove services, charges, and credits and we'll make sure to automate everything, including client reminders.",
        image: {
            src: img2,
            width: 850,
            height: 505
        }
    }
];
function BillingHubRelationshipFetcher(param) {
    let { relationshipId } = param;
    const isVendor = useStore(selectors.user.isVendor);
    const { relationship, isLoading, error, refresh: fetchRelationship } = useRelationship(relationshipId);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const { XERO_INTEGRATION } = useFeatureFlags();
    useOnMount(()=>{
        fetchRelationship({
            silent: true
        });
    });
    if (error) throw makeSentryIgnoredError('Error fetching relationship');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FunctionalLoader, {
                show: isLoading
            }),
            relationship && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.calloutContainer,
                        children: /*#__PURE__*/ _jsx(PageContentHeader, {
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: styles.callouts,
                                children: [
                                    showQboIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.QBO
                                    }),
                                    showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.Xero
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(BillingHub, {
                        isVendor: isVendor,
                        agreement: relationship,
                        refreshAgreement: fetchRelationship
                    })
                ]
            })
        ]
    });
}
export function GlobalBillingHubWithSummaries(props) {
    const { agreements, BH } = props;
    const { urlState: { clientId, relationshipId }, changeUrlState } = useStateFromUrl({
        [BillingHubUrlParams.ClientId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.RelationshipId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.SelectedDate]: {
            defaultValue: ''
        }
    });
    const scrollElRef = useRef(null);
    const [agreementOptions, setAgreementOptions] = useState(()=>getAgreementOptions(agreements, clientId));
    const clientOptions = useMemo(()=>getClientOptions(agreements), [
        agreements
    ]);
    const selectedClient = useMemo(()=>clientId ? clientOptions.find((opt)=>opt.id === clientId) : null, [
        clientId,
        clientOptions
    ]);
    // Clear selected contact if it doesn't exist in the options.
    useEffect(()=>{
        if (clientId && clientOptions.every((o)=>o.id !== clientId)) changeUrlState({
            clientId: ''
        });
    }, [
        clientId,
        clientOptions
    ]);
    // Clear selected agreement if it doesn't exist in the options.
    useEffect(()=>{
        if (relationshipId && agreementOptions.every((o)=>o.id !== relationshipId)) changeUrlState({
            relationshipId: ''
        });
    }, [
        relationshipId,
        agreementOptions
    ]);
    // Automatically populate relationshipId if it can be populated.
    useEffect(()=>{
        if (clientId && !relationshipId && agreementOptions.length) changeUrlState({
            relationshipId: getDefaultRelationshipId(agreementOptions)
        });
    }, [
        clientId,
        relationshipId,
        agreementOptions
    ]);
    const { showModal } = useOverlayContext();
    const { logBI } = useStoreActions();
    const handleBulkBilling = ()=>{
        logBI(18700);
        void showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BulkBillingModal, {
                    dismissModal: dismissModal
                });
            }
        });
    };
    return /*#__PURE__*/ _jsx(Page, {
        header: /*#__PURE__*/ _jsx(GlobalBillingHubHeader, {
            setAgreementOptions: setAgreementOptions,
            changeUrlState: changeUrlState,
            agreements: agreements,
            clientOptions: clientOptions,
            selectedClient: selectedClient,
            agreementOptions: agreementOptions,
            relationshipId: relationshipId
        }),
        ref: scrollElRef,
        actions: !clientId && /*#__PURE__*/ _jsx(Button, {
            skin: "primaryOutline",
            testId: GlobalBillingHubTestIds.BulkBillingButton(),
            onClick: handleBulkBilling,
            children: "Bill clients in bulk"
        }),
        children: relationshipId ? BH || /*#__PURE__*/ _jsx(BillingHubRelationshipFetcher, {
            relationshipId: relationshipId
        }, relationshipId) : /*#__PURE__*/ _jsx(Typography, {
            testId: "gbh-no-agreement",
            className: styles.unselected,
            variant: "paragraph1",
            children: "Please choose a client you have an agreement with to start billing"
        })
    });
}
export const GlobalBillingHubTestIds = pathgen('GlobalBillingHubTestIds');
export default function GlobalBillingHub() {
    const { fetchAgreements } = useStoreActions();
    useSetDocumentTitle('Billing');
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>fetchAgreements(),
        getStoreLocation: (state)=>state.agreements,
        renderLoader: ()=>/*#__PURE__*/ _jsx(Page, {
                children: /*#__PURE__*/ _jsx(LoadingIndicator, {})
            }),
        renderChild: (param)=>{
            let { data } = param;
            return data.length ? /*#__PURE__*/ _jsx(GlobalBillingHubWithSummaries, {
                agreements: data
            }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: "gbh-empty",
                illustration: /*#__PURE__*/ _jsx(GlobalBillingHubEmptyStateIcon, {}),
                title: "Full flexibility with maximum visibility",
                message: "Even fully automated businesses need to get some manual work done. From manual billing to out of agreement charges and credits, with Anchor you have full flexibility.",
                actions: /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                    steps: seeWhatItsLikeSteps
                })
            });
        }
    });
}
